import type { SuretyAccountId } from "@inrev/common";
import { useGetAccountIndemnityAgreementFileUrl } from "@portal/api/account/agent";
import { ConfirmationModalClickWrapper } from "@portal/components/layout/ConfirmationModalClickWrapper";
import { LoadingModal } from "@portal/components/layout/LoadingModal";
import { Button, Icon, Text } from "@portal/components/primitives";
import type { RefObject } from "react";
import { InProgressIndemnityAgreement } from "../../../../../../../account/types";
import { WetSignatureDisclosureCard } from "../../../../../shared/WetSignatureDisclosureCard";
import { IndemnityAgreementFileUpload } from "../components/IndemnityAgreementFileUpload";
import { IndemnityAgreementStatusIndicator } from "../components/IndemnityAgreementStatusIndicator";
import { IndemnityAgreementSectionLayout } from "../layout/IndemnityAgreementLayout";

export type PhysicalIndemnityAgreementSectionProps = {
	accountId: SuretyAccountId;
	indemnityAgreement?: InProgressIndemnityAgreement;
	isIncomplete: boolean;
	signatureMethodComponent: React.ReactNode;
	indemnitorListComponent: React.ReactNode;
	domReadyRef: RefObject<HTMLDivElement | null>;
	onSubmit: () => void;
};

export const PhysicalIndemnityAgreementSection = ({
	accountId,
	indemnityAgreement,
	isIncomplete,
	signatureMethodComponent,
	indemnitorListComponent,
	domReadyRef,
	onSubmit,
}: PhysicalIndemnityAgreementSectionProps) => {
	const { downloadIndemnityAgreement, indemnityAgreementFileUrlLoading } =
		useGetAccountIndemnityAgreementFileUrl({
			accountId,
			indemnityAgreementId: indemnityAgreement?.id,
			asDownload: true,
		});

	const body = (
		<div className="flex flex-col space-y-[30px]">
			{isIncomplete ? (
				<>
					<WetSignatureDisclosureCard />
					<ConfirmationModalClickWrapper
						message="Are you sure you want to generate this indemnity agreement for wet signatures?"
						onConfirm={onSubmit}
						className="max-w-[500px] self-center"
					>
						<Button
							color="primary"
							className="self-center px-[50px]"
							leadingIcon={<Icon type="arrow-down-tray" size="small" color="inverse" />}
						>
							Generate & Download Indemnity Agreement
						</Button>
					</ConfirmationModalClickWrapper>
					{indemnityAgreementFileUrlLoading && <LoadingModal />}
				</>
			) : indemnityAgreement?.id && indemnityAgreement.status !== "incomplete" ? (
				<div className="flex flex-col space-y-9">
					<div className="flex flex-col space-y-5">
						<Text type="title" color="primary">
							Next Steps
						</Text>
						<ol className="flex flex-col space-y-4">
							{indemnityAgreement.status === "pending" ? (
								<>
									<li>
										<Text type="body" color="secondary">
											Download the indemnity agreement by clicking "Download Indemnity Agreement"
											below
										</Text>
									</li>
									<li>
										<Text type="body" color="secondary">
											Collect wet signatures from each signer
										</Text>
									</li>
									<li>
										<Text type="body" color="secondary">
											Upload a scanned copy of the completed indemnity agreement into the box below
										</Text>
									</li>
									<li>
										<Text type="body" color="secondary">
											Mail the original to:
											<br />
											InRev
											<br />
											5806 Grove Avenue #802
											<br />
											Richmond, VA 23226
										</Text>
									</li>
								</>
							) : (
								<li>
									<Text type="body" color="secondary">
										Once an inRev underwriter has reviewed the completed agreement, this page will
										automatically update to show a "Complete" status.
									</Text>
								</li>
							)}
						</ol>
					</div>
					<IndemnityAgreementFileUpload
						indemnityAgreementId={indemnityAgreement.id}
						suretyAccountId={accountId}
						indemnityAgreementStatus={indemnityAgreement.status}
						additionalButtons={
							<Button
								onClick={() => downloadIndemnityAgreement()}
								color="secondary"
								variant="outline"
								loading={indemnityAgreementFileUrlLoading}
							>
								Download Indemnity Agreement
							</Button>
						}
					/>
				</div>
			) : null}
		</div>
	);

	return (
		<IndemnityAgreementSectionLayout
			status={
				<IndemnityAgreementStatusIndicator
					status={isIncomplete ? "incomplete" : (indemnityAgreement?.status ?? "pending")}
					signatureMethod={isIncomplete ? null : (indemnityAgreement?.signatureMethod ?? null)}
				/>
			}
			signatureMethod={signatureMethodComponent}
			requiredIndemnitors={indemnitorListComponent}
			body={body}
			bodyTitle="Indemnity Agreement"
			bodyTestId="agent-account-indemnity-agreement-card"
			domReadyRef={domReadyRef}
		/>
	);
};
